import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Star, Search, Globe, Users, Award, Filter } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getTestimonialsByLocation } from '../data/testimonials';
import TestimonialSlider from '../components/TestimonialSlider';
import { useData } from '@/contexts/DataContext';
import { getTagColor } from '@/data/mock';

const Testimonials = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('All');
  const [selectedRating, setSelectedRating] = useState('All');
  const { testimonials } = useData()
  const locationStats = getTestimonialsByLocation();
  const locations = ['All', ...Object.keys(locationStats)];

  const filteredTestimonials = testimonials?.filter(testimonial => {
    const matchesSearch = testimonial.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         testimonial.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         testimonial.position.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLocation = selectedLocation === 'All' || testimonial.country === selectedLocation;
    const matchesRating = selectedRating === 'All' || testimonial.rating.toString() === selectedRating;
    
    return matchesSearch && matchesLocation && matchesRating;
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { 
      opacity: 0, 
      y: 20,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12,
      },
    },
  };

  return (
    <div className="min-h-screen py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl lg:text-5xl font-bold mb-4">
            Client{' '}
            <span className="bg-gradient-to-r from-purple-600 to-black bg-clip-text text-transparent">
              Testimonials
            </span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Hear what my clients from around the world have to say about working with me.
          </p>
        </motion.div>

        {/* Featured Testimonial Slider */}
        <motion.section 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="mb-20"
        >
          <TestimonialSlider showAll={true} className="max-w-6xl mx-auto" />
        </motion.section>

        {/* Stats */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="grid md:grid-cols-4 gap-6 mb-16"
        >
          <Card className="text-center p-6">
            <CardContent className="p-0">
              <Users className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <div className="text-3xl font-bold text-purple-600">60+</div>
              <div className="text-sm text-muted-foreground">Happy Clients</div>
            </CardContent>
          </Card>
          <Card className="text-center p-6">
            <CardContent className="p-0">
              <Globe className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <div className="text-3xl font-bold text-blue-600">8+</div>
              <div className="text-sm text-muted-foreground">Countries</div>
            </CardContent>
          </Card>
          <Card className="text-center p-6">
            <CardContent className="p-0">
              <Star className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
              <div className="text-3xl font-bold text-yellow-600">5.0</div>
              <div className="text-sm text-muted-foreground">Average Rating</div>
            </CardContent>
          </Card>
          <Card className="text-center p-6">
            <CardContent className="p-0">
              <Award className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <div className="text-3xl font-bold text-green-600">100%</div>
              <div className="text-sm text-muted-foreground">Satisfaction</div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Filters */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="mb-12"
        >
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative w-full lg:w-96">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                type="text"
                placeholder="Search by client, company, or role..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Filters */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <select 
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="bg-background border border-input rounded px-3 py-2 text-sm"
                >
                  {locations?.map(location => (
                    <option key={location} value={location}>
                      {location === 'All' ? 'All Countries' : location}
                    </option>
                  ))}
                </select>
              </div>
              
              <select 
                value={selectedRating}
                onChange={(e) => setSelectedRating(e.target.value)}
                className="bg-background border border-input rounded px-3 py-2 text-sm"
              >
                <option value="All">All Ratings</option>
                <option value="5">5 Stars</option>
                <option value="4">4+ Stars</option>
              </select>
            </div>
          </div>
        </motion.div>

        {/* Testimonials Grid */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          <AnimatePresence>
            {filteredTestimonials?.map((testimonial, index) => (
              <motion.div
                key={testimonial?._id}
                variants={itemVariants}
                layout
                whileHover={{ y: -5, scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <Card className="h-full hover:shadow-xl transition-all duration-300">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-4">
                      <img
                        src={testimonial.images?.url}
                        alt={testimonial?.projectTitle}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div className="flex-1">
                        <CardTitle className="text-lg">{testimonial.name}</CardTitle>
                        <p className="text-sm text-muted-foreground">{testimonial.position}</p>
                        <p className="text-sm font-medium text-purple-600">{testimonial.company}</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          ))}
                        </div>
                        <Badge className={getTagColor(testimonial.country)}>
                          {testimonial.country} Client
                        </Badge>
                      </div>
                      
                      <blockquote className="text-sm text-muted-foreground italic leading-relaxed">
                        "{testimonial.message}"
                      </blockquote>
                      
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>{testimonial.projectTitle}</span>
                        <span>{testimonial.country}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* No Results */}
        {filteredTestimonials?.length === 0 && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-semibold mb-2">No testimonials found</h3>
            <p className="text-muted-foreground mb-6">
              Try adjusting your search or filter criteria.
            </p>
            <Button 
              variant="outline" 
              onClick={() => {
                setSearchTerm('');
                setSelectedLocation('All');
                setSelectedRating('All');
              }}
            >
              Clear Filters
            </Button>
          </motion.div>
        )}

        {/* CTA Section */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="mt-20 text-center py-16 bg-gradient-to-r from-purple-50 to-black/5 dark:from-purple-950/20 dark:to-black/40 rounded-3xl"
        >
          <h2 className="text-3xl font-bold mb-4">Ready to Join These Happy Clients?</h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Let's discuss your project and see how I can help bring your ideas to life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-purple-600 hover:bg-purple-700">
              Start Your Project
            </Button>
            <Button size="lg" variant="outline">
              View My Work
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Testimonials;